import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import api from 'services/api'
import Refreshing from 'components/core/Refreshing'
import EChart from '../Cost/EChart'
import { useTabRead } from '../Flow/reads'
import { fmtCount } from '../StreamIngest/vocabulary'
import { fmtShare, share } from './vocabulary'
import {
  END_LABEL_LAYOUT,
  TOOLTIP,
  axisStyle,
  useChartStyle,
} from './chartStyle'

// The Listens tab: is a quote that is seen heard, and heard through (reclip-api
// `stream/metrics/loop/listens.py`, after the proto console's fleet tab).
// Three questions, one form each:
//
// - **Did people come back to hear something?** People whose phones reported
//   that day beside those who heard a quote: the second against the first.
// - **Is a quote that is seen heard, and heard through?** A survival curve per
//   group: the share of first listens still going at each twentieth of the
//   quote. Where two lines part is where one group loses people. One control
//   picks the split; the table under it is the curve's twin, plus what a curve
//   cannot carry: seen, heard again, the wait, how listens ended.
// - **Is listening a habit?** People by the days they heard a quote: a pile
//   at one day is a novelty, a tail to the right a habit.
//
// Every figure is over people whose phones report, and says so.

const Section = ({ title, caption, children, action }) => (
  <Paper
    variant="outlined"
    sx={{ p: { mobile: 2, desktop: 3 }, borderRadius: 2, mb: 3 }}
  >
    <Box
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
    {caption && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 900 }}
      >
        {caption}
      </Typography>
    )}
    {children}
  </Paper>
)

const Key = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
    {items.map(({ label, color }) => (
      <Box
        key={label}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        <Box
          sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }}
        />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
)

// ---- did people come back -------------------------------------------------

const Daily = ({ daily }) => {
  const style = useChartStyle()
  const option = useMemo(
    () => ({
      grid: { left: 36, right: 16, top: 16, bottom: 24 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...TOOLTIP,
        formatter: ([point]) => {
          const row = daily[point.dataIndex]
          return `<div style="margin-bottom:4px">${row.day} UTC</div>
            ${fmtCount(row.heard)} of ${fmtCount(row.present)} heard a quote
            <div style="opacity:.75">${fmtCount(row.listens)} listens</div>`
        },
      },
      xAxis: {
        type: 'category',
        data: daily.map(({ day }) => day),
        ...axisStyle(style),
        axisLabel: {
          ...axisStyle(style).axisLabel,
          formatter: (d) => d.slice(5),
        },
        splitLine: { show: false },
      },
      yAxis: { type: 'value', minInterval: 1, ...axisStyle(style) },
      series: [
        {
          name: 'reported',
          type: 'bar',
          barMaxWidth: 24,
          barGap: '10%',
          itemStyle: { color: style.muted, borderRadius: [4, 4, 0, 0] },
          data: daily.map(({ present }) => present),
        },
        {
          name: 'heard a quote',
          type: 'bar',
          barMaxWidth: 24,
          itemStyle: { color: style.accent, borderRadius: [4, 4, 0, 0] },
          data: daily.map(({ heard }) => heard),
        },
      ],
    }),
    [daily, style]
  )
  return (
    <>
      <Key
        items={[
          { label: 'phones reported', color: style.muted },
          { label: 'heard a quote', color: style.accent },
        ]}
      />
      <EChart
        option={option}
        height={220}
        isEmpty={!daily.some(({ present }) => present)}
        emptyText="No phone reported in this window."
      />
    </>
  )
}

// ---- heard through ----------------------------------------------------------

const under = (ms) => (ms == null ? 'over 5 s' : `under ${ms / 1000} s`)

const Survival = ({ lenses, steps }) => {
  const style = useChartStyle()
  const [lensKey, setLensKey] = useState('whose')
  const lens = lenses.find(({ key }) => key === lensKey) || lenses[0]
  // A group's colour is its place in the lens, drawn or not, so a colour
  // always means one group.
  const colorOf = (group) =>
    style.series[(lens?.groups || []).indexOf(group) % style.series.length]
  const groups = useMemo(
    () => (lens?.groups || []).filter(({ first }) => first > 0),
    [lens]
  )
  const marks = useMemo(
    () =>
      Array.from({ length: steps + 1 }, (_, k) =>
        Math.round((k / steps) * 100)
      ),
    [steps]
  )
  const option = useMemo(
    () => ({
      grid: { left: 44, right: 120, top: 16, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        ...TOOLTIP,
        formatter: (points) => {
          const k = points[0]?.dataIndex ?? 0
          return `<div style="margin-bottom:4px">${
            marks[k]
          }% into the quote, still listening:</div>${points
            .map(({ seriesIndex, marker }) => {
              const g = groups[seriesIndex]
              return `${marker}${g.label}: ${fmtCount(
                g.reached[k]
              )} of ${fmtCount(g.first)} (${fmtShare(
                share(g.reached[k], g.first)
              )})`
            })
            .join('<br/>')}`
        },
      },
      xAxis: {
        type: 'category',
        data: marks,
        boundaryGap: false,
        ...axisStyle(style),
        axisLabel: {
          ...axisStyle(style).axisLabel,
          formatter: (v) => `${v}%`,
          interval: 4,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        ...axisStyle(style),
        axisLabel: { ...axisStyle(style).axisLabel, formatter: (v) => `${v}%` },
      },
      series: groups.map((group) => {
        const color =
          style.series[
            (lens?.groups || []).indexOf(group) % style.series.length
          ]
        return {
          name: group.label,
          type: 'line',
          step: 'end',
          symbol: 'none',
          lineStyle: { width: 2, color },
          itemStyle: { color },
          data: group.reached.map(
            (n) => Math.round((n / group.first) * 1000) / 10
          ),
          labelLayout: END_LABEL_LAYOUT,
          endLabel: {
            show: true,
            formatter: () => group.label,
            color: style.text,
            fontSize: 11,
          },
        }
      }),
    }),
    [lens, groups, marks, style]
  )
  return (
    <Section
      title="Is a quote that is seen heard, and heard through?"
      caption="Each line is first listens still going at each point of the quote. Where two lines part is where one group loses people: early, the quote starts in the wrong place; steadily, it runs long."
      action={
        <ToggleButtonGroup
          value={lens?.key}
          exclusive
          size="small"
          onChange={(event, next) => next && setLensKey(next)}
        >
          {lenses.map(({ key, label }) => (
            <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      <EChart
        option={option}
        height={260}
        isEmpty={groups.length === 0}
        emptyText="No first listen in this window."
      />
      <Box sx={{ overflowX: 'auto', mt: 2 }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">seen</TableCell>
              <TableCell align="right">heard</TableCell>
              <TableCell align="right">first listens</TableCell>
              <TableCell align="right">to the end</TableCell>
              <TableCell align="right">heard again</TableCell>
              <TableCell align="right">first sound</TableCell>
              <TableCell>how first listens ended</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(lens?.groups || []).map((g) => (
              <TableRow key={g.key}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        bgcolor: colorOf(g),
                        flexShrink: 0,
                      }}
                    />
                    {g.label}
                  </Box>
                </TableCell>
                <TableCell align="right">{fmtCount(g.seen)}</TableCell>
                <TableCell align="right">
                  {fmtCount(g.heard)}{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    {fmtShare(share(g.heard, g.seen))}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmtCount(g.first)}</TableCell>
                <TableCell align="right">
                  {fmtShare(share(g.reached[g.reached.length - 1], g.first))}
                </TableCell>
                <TableCell align="right">
                  {fmtShare(share(g.again, g.first))}
                </TableCell>
                <TableCell align="right">
                  {g.first ? `median ${under(g.wait_p50_ms)}` : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {g.first
                      ? g.ends
                          .filter(({ n }) => n)
                          .map(
                            ({ label, n }) =>
                              `${label} ${fmtShare(share(n, g.first))}`
                          )
                          .join(' · ')
                      : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1.5 }}
      >
        Seen is quotes on screen for a second, once per phone; heard is how many
        of those the person heard anywhere in the window. A first listen is a
        phone’s first of a quote; cut off is a call, a lost audio route or the
        app dying, never the listener’s choice.
      </Typography>
    </Section>
  )
}

// ---- a habit ------------------------------------------------------------------

const Habit = ({ habit }) => {
  const style = useChartStyle()
  const option = useMemo(
    () => ({
      grid: { left: 36, right: 16, top: 16, bottom: 36 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...TOOLTIP,
        formatter: ([point]) =>
          `${fmtCount(point.value)} ${
            point.value === 1 ? 'person' : 'people'
          } heard a quote on ${point.dataIndex} day${
            point.dataIndex === 1 ? '' : 's'
          }`,
      },
      xAxis: {
        type: 'category',
        data: habit.map((_, k) => k),
        name: 'days with a listen',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { color: style.text, fontSize: 10 },
        ...axisStyle(style),
        splitLine: { show: false },
      },
      yAxis: { type: 'value', minInterval: 1, ...axisStyle(style) },
      series: [
        {
          type: 'bar',
          barMaxWidth: 24,
          itemStyle: { color: style.accent, borderRadius: [4, 4, 0, 0] },
          data: habit,
        },
      ],
    }),
    [habit, style]
  )
  return (
    <Section
      title="Is listening a habit?"
      caption="People whose phones reported, by the number of days they heard a quote. A pile at one day is a novelty; a tail to the right is a habit."
    >
      <EChart
        option={option}
        height={200}
        isEmpty={habit.length === 0}
        emptyText="No phone reported in this window."
      />
    </Section>
  )
}

const Listens = ({ days, refreshKey }) => {
  const { answer, error, loading } = useTabRead(
    api.streamMetrics.getLoopListens,
    {
      days,
      refreshKey,
    }
  )
  if (error)
    return (
      <Alert severity="warning">
        The event table could not be read: {error}
      </Alert>
    )
  const totals = answer?.totals
  return (
    <Refreshing active={loading}>
      {answer && (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 900 }}
          >
            {fmtCount(totals.people)}{' '}
            {totals.people === 1 ? 'person’s phone' : 'people’s phones'}{' '}
            reported in this window
            {totals.builds.length
              ? ` (builds ${totals.builds.join(', ')})`
              : ''}
            : {fmtCount(totals.listeners)} heard a quote,{' '}
            {fmtCount(totals.listens)} listens. Every figure here is over phones
            that report.
            {answer.read_at
              ? ` Read ${dayjs(answer.read_at).format(
                  'HH:mm'
                )}, kept five minutes.`
              : ''}
          </Typography>
          <Section
            title="Did people come back to hear something?"
            caption="Each day, the people whose phones reported anything, and those who heard a quote."
          >
            <Daily daily={answer.daily} />
          </Section>
          <Survival lenses={answer.lenses} steps={answer.steps} />
          <Habit habit={answer.habit} />
        </>
      )}
    </Refreshing>
  )
}

export default Listens
